import { useState } from 'react';

export type ValidationRules = {
  [key: string]: {
    required?: boolean;
    validator?: (value: string, allValues: FormValues) => boolean;
    errorMessage?: string;
    requiredMessage?: string;
  };
};

type FormValues = {
  [key: string]: string;
};

type FormErrors = {
  [key: string]: string;
};

type FormTouched = {
  [key: string]: boolean;
};

const useFormValidation = (initialValues: FormValues, validationRules: ValidationRules) => {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<FormTouched>(
    Object.keys(initialValues).reduce((acc, key) => ({...acc, [key]: false}), {})
  );
  
  // Validate a single field
  const validateField = (name: string, value: string): string => {
    const rules = validationRules[name];
    if (!rules) return '';
    
    if (rules.required && !value.trim()) {
      return rules.requiredMessage || 'This field is required and cannot be blank.';
    }
    
    if (rules.validator && value.trim() && !rules.validator(value, values)) {
      return rules.errorMessage || 'Invalid input';
    }
    
    return '';
  };
  
  // Validate all fields
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;
    
    Object.keys(values).forEach(name => {
      const error = validateField(name, values[name]);
      if (error) {
        newErrors[name] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    // Mark all fields as touched when form is validated
    setTouched(Object.keys(values).reduce((acc, key) => ({...acc, [key]: true}), {}));
    
    return isValid;
  };
  
  // Handle input change
  const handleInputChange = (name: string, value: string) => {
    setValues(prev => ({...prev, [name]: value}));
    
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({...prev, [name]: error}));
    }
  };
  
  // Handle blur
  const handleBlur = (name: string) => {
    setTouched(prev => ({...prev, [name]: true}));
    const error = validateField(name, values[name]);
    setErrors(prev => ({...prev, [name]: error}));
  };
  
  // Reset form
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setTouched(Object.keys(initialValues).reduce((acc, key) => ({...acc, [key]: false}), {}));
  };
  
  // Check if form is valid
  const isFormValid = (): boolean => {
    return Object.keys(validationRules).every(field => {
      const value = values[field] ?? '';
      const error = validateField(field, value);
      return value.trim() !== '' && !error;
    });
  };
  
  return {
    values,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    isFormValid,
    resetForm
  };
};

export default useFormValidation;
